/**
 * CountdownToastView — presentation for `butterToast.countdown`.
 *
 * A generic countdown-action toast: title + optional message + an optional countdown
 * progress bar + a primary action and a cancel. When `seconds` is set the bar drains and
 * fires `onComplete` at zero (idle self-commit); the action button fires it immediately;
 * cancel dismisses without firing. When `seconds` is unset it's a plain action/cancel
 * toast. Kept separate from the frozen {@link ToastView} contract.
 */
import { useEffect, useRef, useState, type FocusEvent } from 'react'
import { toast as sonner } from 'sonner'

import { RotateIcon } from '#icons'
import { Progress } from '#primitives/feedback'
// Import cn from its file, not the `#utils` barrel — see ToastView for the cycle rationale.
import { cn } from '../../utils/cn'

import type { ToastId, ToastVariant } from './types'

export interface CountdownToastViewProps {
  /** sonner's toast id for this instance — used to dismiss. */
  id: ToastId
  title: string
  message?: string
  /** Countdown length in seconds. Omit (or 0) for a manual action/cancel toast. */
  seconds?: number
  /** Primary button label. @default 'Confirm' */
  actionLabel?: string
  /** Cancel button label. @default 'Cancel' */
  cancelLabel?: string
  /** Visual intent (drives the icon-box color). @default 'info' */
  variant?: ToastVariant
  /** Fired when the countdown reaches zero or the action button is clicked (once). */
  onComplete: () => void
}

const TICK_MS = 200

export function CountdownToastView({
  id,
  title,
  message,
  seconds,
  actionLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'info',
  onComplete,
}: CountdownToastViewProps) {
  const hasCountdown = typeof seconds === 'number' && seconds > 0
  const totalMs = hasCountdown ? (seconds as number) * 1000 : 0

  const [isPaused, setIsPaused] = useState(false)
  const [remainingMs, setRemainingMs] = useState(totalMs)
  const remainingRef = useRef(totalMs) // budget carried across pause/resume
  const completedRef = useRef(false)

  // Committing also closes the toast. It mounts with `duration: Infinity` — the
  // countdown owns closing — so without this a consumer whose `onComplete` stays on the
  // page (a client-side route change, say) leaves the toast frozen at "(0)" forever.
  // `onComplete` runs first so a throwing consumer leaves the toast up as a visible
  // symptom rather than failing silently.
  const complete = () => {
    if (completedRef.current) return
    completedRef.current = true
    onComplete()
    sonner.dismiss(id)
  }

  useEffect(() => {
    if (!hasCountdown || isPaused) return
    const budget = remainingRef.current
    const start = Date.now()
    const interval = window.setInterval(() => {
      const left = Math.max(0, budget - (Date.now() - start))
      setRemainingMs(left)
      if (left <= 0) {
        window.clearInterval(interval)
        remainingRef.current = 0
        complete()
      }
    }, TICK_MS)
    return () => {
      window.clearInterval(interval)
      // On pause (or unmount) remember the time left so a resume continues accurately.
      if (!completedRef.current) {
        remainingRef.current = Math.max(0, budget - (Date.now() - start))
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasCountdown, isPaused, totalMs])

  // Pause the auto-refresh while the user is reading or interacting (hover/focus) so the
  // countdown never yanks the page out from under them; Cancel stays the hard escape.
  const pause = () => setIsPaused(true)
  const resume = () => setIsPaused(false)
  const handleBlur = (e: FocusEvent<HTMLDivElement>) => {
    // Resume only when focus truly leaves the toast, not when moving between its buttons.
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setIsPaused(false)
  }

  const remainingSeconds = Math.ceil(remainingMs / 1000)
  const percentLeft = hasCountdown && totalMs > 0 ? (remainingMs / totalMs) * 100 : 0

  return (
    <div
      data-variant={variant}
      role="status"
      onPointerEnter={pause}
      onPointerLeave={resume}
      onFocus={pause}
      onBlur={handleBlur}
      className={cn(
        'group flex w-full flex-col gap-3 rounded-xl border border-butter-gray-100 bg-white p-3 shadow-dropshadow-50',
      )}
    >
      {/* items-center so the tile stays balanced when the message wraps. */}
      <div className="flex items-center gap-2.5">
        <span
          aria-hidden
          className={cn(
            'flex size-10 shrink-0 items-center justify-center rounded-lg',
            'group-data-[variant=info]:bg-butter-blue-25 group-data-[variant=info]:text-butter-blue-500',
            'group-data-[variant=success]:bg-butter-success-200 group-data-[variant=success]:text-butter-success-600',
            'group-data-[variant=error]:bg-butter-error-100 group-data-[variant=error]:text-butter-error-500',
          )}
        >
          <RotateIcon className="size-6.5" />
        </span>

        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <p className="text-lg font-semibold leading-tight text-butter-gray-900">{title}</p>
          {message ? (
            <p className="text-sm font-medium leading-snug text-butter-gray-600">{message}</p>
          ) : null}
        </div>
      </div>

      {hasCountdown ? (
        <Progress
          value={percentLeft}
          className="h-1.5"
          // Follow the runtime white-label theme (falls back to the purple brand default).
          indicatorClassName="bg-theme-600"
          aria-label={`Auto-refreshing in ${remainingSeconds} second${remainingSeconds === 1 ? '' : 's'}${isPaused ? ' (paused)' : ''}`}
        />
      ) : null}

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={complete}
          className="text-interactive:bg-theme-700 rounded-lg bg-theme-600 px-3 py-1.5 text-sm font-semibold text-white"
        >
          {actionLabel}
          {hasCountdown ? ` (${remainingSeconds})` : ''}
        </button>
        <button
          type="button"
          onClick={() => sonner.dismiss(id)}
          className="text-interactive:text-butter-gray-900 px-3 py-1.5 text-sm font-semibold text-butter-gray-600"
        >
          {cancelLabel}
        </button>
      </div>
    </div>
  )
}
